import { Amplify, API, Auth } from 'aws-amplify';
import awsconfig from '../aws-exports';
import { detectUserCountry } from '../utils/detectCountry';

// Configure Amplify
Amplify.configure(awsconfig);

// Helper function to get auth headers for API calls
async function getAuthHeaders() {
  try {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();
    console.log('getAuthHeaders: Got auth token (length:', token.length, ')');
    return {
      Authorization: `Bearer ${token}`
    };
  } catch (err) {
    console.log('getAuthHeaders: Not authenticated or session expired:', err.message);
    return {};
  }
}

// Helper function to get or create session ID
function getSessionId() {
  let sessionId = localStorage.getItem('printify_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('printify_session_id', sessionId);
  }
  return sessionId;
}

// Helper function to convert file to base64
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

// Real Amplify API
export const amplifyApi = {
  uploadImage: async (file) => {
    try {
      const base64Image = await fileToBase64(file);

      const response = await API.post('printifyapi', '/api/images/upload', {
        body: {
          image: base64Image,
          sessionId: getSessionId(),
          filename: file.name,
          contentType: file.type
        }
      });

      return response;
    } catch (error) {
      console.error('Upload error:', error);
      // If API fails, fall back to mock
      console.log('Falling back to mock data');
      return mockApi.uploadImage(file);
    }
  },

  // Upload a data URL (base64) image directly - used for saved designs and product creation
  // For large images (>5MB), uses presigned URL upload to bypass API Gateway limits
  uploadDataUrl: async (dataUrl, filename = 'design.png') => {
    try {
      // Determine content type from data URL
      const contentType = dataUrl.startsWith('data:image/png') ? 'image/png' :
                          dataUrl.startsWith('data:image/jpeg') ? 'image/jpeg' :
                          dataUrl.startsWith('data:image/webp') ? 'image/webp' : 'image/png';

      // Estimate base64 size (data URL overhead is about 37% of original)
      const base64Part = dataUrl.split(',')[1] || '';
      const approximateSizeMB = (base64Part.length * 0.75) / (1024 * 1024);

      console.log('uploadDataUrl: size estimate', approximateSizeMB.toFixed(2), 'MB');

      // For large images, use presigned URL upload to bypass API Gateway 6MB limit
      if (approximateSizeMB > 5) {
        console.log('Large image detected, using presigned URL upload for Printify...');

        const headers = await getAuthHeaders();

        // Get presigned URL for S3 upload
        const fileExtension = contentType === 'image/jpeg' ? 'jpg' :
                              contentType === 'image/webp' ? 'webp' : 'png';

        const urlResponse = await API.post('printifyapi', '/api/images/get-upload-url', {
          headers,
          body: {
            contentType,
            fileExtension,
            forPrintify: true // Flag to indicate this is for Printify upload
          }
        });

        if (!urlResponse.success || !urlResponse.uploadUrl) {
          throw new Error(urlResponse.error || 'Failed to get presigned upload URL');
        }

        // Convert base64 to blob
        const byteCharacters = atob(base64Part);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });

        console.log('Uploading to S3 via presigned URL, blob size:', (blob.size / 1024 / 1024).toFixed(2), 'MB');

        // Upload directly to S3
        const uploadResponse = await fetch(urlResponse.uploadUrl, {
          method: 'PUT',
          body: blob,
          headers: {
            'Content-Type': contentType
          }
        });

        if (!uploadResponse.ok) {
          throw new Error(`S3 upload failed: ${uploadResponse.status}`);
        }

        console.log('S3 upload successful, now uploading to Printify via S3 URL...');

        // Now tell the backend to upload from S3 to Printify
        const printifyResponse = await API.post('printifyapi', '/api/images/upload-from-s3', {
          headers,
          body: {
            s3Key: urlResponse.s3Key,
            filename,
            contentType
          }
        });

        return printifyResponse;
      }

      // For smaller images, use direct upload
      const response = await API.post('printifyapi', '/api/images/upload', {
        body: {
          image: dataUrl,
          sessionId: getSessionId(),
          filename: filename,
          contentType: contentType
        }
      });

      return response;
    } catch (error) {
      console.error('Upload data URL error:', error);
      throw error;
    }
  },

  createProducts: async (imageId, imageData, selectedCategories = null) => {
    try {
      const headers = await getAuthHeaders();
      const body = {
        imageId,
        sessionId: getSessionId(),
        image: imageData, // Pass the full image data including printifyImageId
        userCountry: detectUserCountry() // e.g., "GB", "US", or null — for provider region selection
      };

      // Add selected categories if provided
      if (selectedCategories && selectedCategories.length > 0) {
        body.selectedCategories = selectedCategories;
      }

      // Debug: Log what we're sending
      console.log('Creating products with image data:', {
        imageId,
        printifyImageId: imageData?.printifyImageId,
        width: imageData?.width,
        height: imageData?.height,
        url: imageData?.url ? imageData.url.substring(0, 50) + '...' : 'missing',
        hasAuthHeader: !!headers.Authorization
      });

      const response = await API.post('printifyapi', '/api/products/create-for-image', {
        headers,
        body
      });

      // Check if the response indicates failure
      if (!response.success && response.message) {
        // Log debug info if available
        if (response.debug) {
          console.log('Product creation debug info:', response.debug);
        }
        throw new Error(response.message);
      }

      return response;
    } catch (error) {
      console.error('Product creation error:', error);
      // Don't fall back to mock data - throw the error so frontend can handle it
      throw error;
    }
  },

  // Get available product categories
  getProductCategories: async () => {
    try {
      const response = await API.get('printifyapi', '/api/products/categories', {});
      return response;
    } catch (error) {
      console.error('Error fetching product categories:', error);
      // Return mock categories as fallback
      return {
        success: true,
        categories: [
          { id: 't-shirt', name: 'T-Shirt', blueprintCount: 50 },
          { id: 'hoodie', name: 'Hoodie', blueprintCount: 25 },
          { id: 'mug', name: 'Mug', blueprintCount: 15 },
          { id: 'tote', name: 'Tote', blueprintCount: 10 },
          { id: 'poster', name: 'Poster', blueprintCount: 20 },
          { id: 'phone-case', name: 'Phone Case', blueprintCount: 30 },
          { id: 'sticker', name: 'Sticker', blueprintCount: 12 },
          { id: 'hat', name: 'Hat', blueprintCount: 8 }
        ]
      };
    }
  },

  // Generate image with AI
  // SECURED: Requires authentication
  generateImage: async (prompt) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', '/api/images/generate', {
        headers,
        body: { prompt }
      });
      return response;
    } catch (error) {
      console.error('Error generating image:', error);
      // Return structured error for frontend handling
      const errorData = error.response?.data || {};
      if (errorData.error === 'AUTH_REQUIRED' || errorData.error === 'RATE_LIMITED') {
        return {
          success: false,
          error: errorData.error,
          message: errorData.message
        };
      }
      throw error;
    }
  },

  // Edit image with AI (including background removal)
  // SECURED: Requires authentication
  // Handles large images via presigned S3 upload to avoid API Gateway limits
  editImage: async (imageData, action = 'edit', prompt = null) => {
    try {
      const headers = await getAuthHeaders();

      // Check if imageData is a data URL and estimate size
      let imageUrl = imageData?.url || imageData;
      const isDataUrl = typeof imageUrl === 'string' && imageUrl.startsWith('data:');

      if (isDataUrl) {
        // Extract base64 and estimate size
        const base64Part = imageUrl.split(',')[1] || '';
        const approximateSizeMB = (base64Part.length * 0.75) / (1024 * 1024);
        console.log(`Edit image size estimate: ${approximateSizeMB.toFixed(2)}MB`);

        // If image is large (>4MB), upload to S3 first and pass the S3 key
        if (approximateSizeMB > 4) {
          console.log('Image too large for direct API call, uploading to S3 first...');

          // Get presigned URL for upload
          const urlResponse = await API.post('printifyapi', '/api/images/get-upload-url', {
            headers,
            body: {
              contentType: imageUrl.split(';')[0].split(':')[1] || 'image/png',
              fileExtension: 'png',
              forPrintify: false  // Store in designs folder
            }
          });

          if (!urlResponse.success || !urlResponse.uploadUrl) {
            throw new Error('Failed to get presigned upload URL');
          }

          // Convert base64 to blob
          const byteString = atob(base64Part);
          const byteArray = new Uint8Array(byteString.length);
          for (let i = 0; i < byteString.length; i++) {
            byteArray[i] = byteString.charCodeAt(i);
          }
          const contentType = imageUrl.split(';')[0].split(':')[1] || 'image/png';
          const blob = new Blob([byteArray], { type: contentType });

          // Upload directly to S3
          console.log('Uploading large image to S3...');
          const uploadResponse = await fetch(urlResponse.uploadUrl, {
            method: 'PUT',
            body: blob,
            headers: {
              'Content-Type': contentType
            }
          });

          if (!uploadResponse.ok) {
            throw new Error(`S3 upload failed: ${uploadResponse.status}`);
          }

          console.log('S3 upload successful, calling edit API with S3 key...');

          // Call edit endpoint with S3 key instead of data URL
          const response = await API.post('printifyapi', '/api/images/edit', {
            headers,
            body: {
              s3Key: urlResponse.s3Key,  // Pass S3 key instead of data URL
              action,
              prompt
            }
          });
          return response;
        }
      }

      // For smaller images or HTTP URLs, use direct API call
      const response = await API.post('printifyapi', '/api/images/edit', {
        headers,
        body: {
          image: imageData,
          action,
          prompt
        }
      });
      return response;
    } catch (error) {
      console.error('Error editing image:', error);
      // Return structured error for frontend handling
      const errorData = error.response?.data || {};
      if (errorData.error === 'AUTH_REQUIRED' || errorData.error === 'RATE_LIMITED') {
        return {
          success: false,
          error: errorData.error,
          message: errorData.message
        };
      }
      // Extract more meaningful error message
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.details ||
                          error.response?.data?.message ||
                          error.message ||
                          'Image editing failed';
      const enhancedError = new Error(errorMessage);
      enhancedError.originalError = error;
      throw enhancedError;
    }
  },

  // Remove background from image (convenience method)
  removeBackground: async (imageData) => {
    return amplifyApi.editImage(imageData, 'remove-background');
  },

  initiateCheckout: async (productId) => {
    try {
      const response = await API.post('printifyapi', '/api/products/checkout', {
        body: {
          productId,
          sessionId: getSessionId()
        }
      });

      return response;
    } catch (error) {
      console.error('Checkout error:', error);
      // If API fails, fall back to mock
      console.log('Falling back to mock data');
      return mockApi.initiateCheckout(productId);
    }
  },

  // Delete a product
  deleteProduct: async (productId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.del('printifyapi', `/api/products/${productId}`, { headers });
      return response;
    } catch (error) {
      console.error('Delete product error:', error);
      console.log('Falling back to mock data');
      return mockApi.deleteProduct(productId);
    }
  },

  // Claim an orphaned product (product without owner)
  claimProduct: async (productId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', `/api/products/${productId}/claim`, {
        headers,
        body: {}
      });
      return response;
    } catch (error) {
      console.error('Claim product error:', error);
      return { success: false, error: error.message || 'Failed to claim product' };
    }
  },

  // Get published products - uses new DynamoDB-first gallery endpoint
  getPublishedProducts: async () => {
    try {
      const headers = await getAuthHeaders();
      // Use new gallery endpoint (DynamoDB-first, faster)
      const response = await API.get('printifyapi', '/api/products/gallery', { headers });
      console.log(`[Gallery] Loaded ${response.total} products (${response.dynamoCount} from DynamoDB, ${response.legacyCount} legacy) in ${response.responseTimeMs}ms`);
      return response;
    } catch (error) {
      console.error('Error fetching gallery products:', error);
      // Fall back to old endpoint if new one fails
      try {
        console.log('Falling back to /api/products/published endpoint');
        const headers = await getAuthHeaders();
        return await API.get('printifyapi', '/api/products/published', { headers });
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        return mockApi.getPublishedProducts();
      }
    }
  },

  // Publish a product
  publishProduct: async (productId) => {
    try {
      const response = await API.post('printifyapi', `/api/products/${productId}/publish`, {
        body: {}
      });
      return response;
    } catch (error) {
      console.error('Error publishing product:', error);
      console.log('Falling back to mock data');
      return mockApi.publishProduct(productId);
    }
  },

  // Create an order
  createOrder: async (productId, variantId, customerInfo, quantity = 1) => {
    try {
      const response = await API.post('printifyapi', '/api/orders/create', {
        body: {
          productId,
          variantId,
          customerInfo,
          quantity
        }
      });
      return response;
    } catch (error) {
      console.error('Error creating order:', error);
      console.log('Falling back to mock data');
      return mockApi.createOrder(productId, variantId, customerInfo, quantity);
    }
  },

  // Get order status
  getOrder: async (orderId) => {
    try {
      const response = await API.get('printifyapi', `/api/orders/${orderId}`, {});
      return response;
    } catch (error) {
      console.error('Error fetching order:', error);
      console.log('Falling back to mock data');
      return mockApi.getOrder(orderId);
    }
  },

  // Get all orders
  getOrders: async (page = 1, limit = 20) => {
    try {
      const response = await API.get('printifyapi', `/api/orders?page=${page}&limit=${limit}`, {});
      return response;
    } catch (error) {
      console.error('Error fetching orders:', error);
      console.log('Falling back to mock data');
      return mockApi.getOrders(page, limit);
    }
  },

  // Get available colors for a product
  getProductColors: async (productId) => {
    try {
      const response = await API.get('printifyapi', `/api/products/${productId}/colors`, {});
      return response;
    } catch (error) {
      console.error('Error fetching product colors:', error);
      console.log('Falling back to mock data');
      return mockApi.getProductColors(productId);
    }
  },

  // Get blueprint templates for a product
  getProductTemplates: async (productId) => {
    try {
      const response = await API.get('printifyapi', `/api/products/${productId}/templates`, {});
      return response;
    } catch (error) {
      console.error('Error fetching product templates:', error);
      return {
        success: false,
        templates: [],
        error: error.message || 'Failed to fetch product templates'
      };
    }
  },

  // Get a single product by ID (for product pages/sharing)
  getProduct: async (productId) => {
    try {
      const response = await API.get('printifyapi', `/api/products/${productId}/details`, {});
      if (response && response.product) {
        return { success: true, product: response.product };
      }
      return { success: false, error: 'Product not found' };
    } catch (error) {
      console.error('Error fetching product:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch product'
      };
    }
  },

  // Get user's public shop page with their products (returns all products, frontend handles pagination)
  getShop: async (userId) => {
    try {
      const response = await API.get('printifyapi', `/api/shop/${userId}`, {});
      return response;
    } catch (error) {
      console.error('Error fetching shop:', error);
      // Handle 404 specifically
      if (error.response?.status === 404) {
        return { success: false, error: 'Shop not found', notFound: true };
      }
      return {
        success: false,
        error: error.message || 'Failed to fetch shop'
      };
    }
  },

  // Get product details including current placement
  getProductDetails: async (productId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', `/api/products/${productId}/details`, { headers });
      return response;
    } catch (error) {
      console.error('Error fetching product details:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch product details'
      };
    }
  },

  // Update product image placement (scale, position, rotation)
  // Optionally accepts position to update only that specific print area
  updateProductPlacement: async (productId, placement, position = null) => {
    try {
      const headers = await getAuthHeaders();
      const body = { ...placement };
      if (position) {
        body.position = position;
      }
      console.log('updateProductPlacement request:', { productId, body, hasAuth: !!headers.Authorization });
      const response = await API.put('printifyapi', `/api/products/${productId}/placement`, {
        headers,
        body
      });
      console.log('updateProductPlacement raw response:', response);
      // Handle case where response might be undefined
      if (!response) {
        console.error('Empty response from placement update');
        return { success: false, error: 'No response from server' };
      }
      return response;
    } catch (error) {
      console.error('Error updating product placement:', error);
      // Check if it's an API Gateway error with more details
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      return {
        success: false,
        error: error.message || 'Failed to update product placement'
      };
    }
  },

  // Add design to a new print position (e.g., add to back)
  addDesignToPosition: async (productId, position, placement = {}) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', `/api/products/${productId}/placement/${position}`, {
        headers,
        body: placement
      });
      return response;
    } catch (error) {
      console.error('Error adding design to position:', error);
      // Extract error message from API response body if available
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to add design to position';
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // Remove design from a print position
  removeDesignFromPosition: async (productId, position) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.del('printifyapi', `/api/products/${productId}/placement/${position}`, { headers });
      return response;
    } catch (error) {
      console.error('Error removing design from position:', error);
      // Extract error message from API response body if available
      const errorMessage = error.response?.data?.error ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to remove design from position';
      return {
        success: false,
        error: errorMessage
      };
    }
  },

  // Update product visibility (public/private)
  updateProductVisibility: async (productId, visibility) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', `/api/products/${productId}/visibility`, {
        headers,
        body: { visibility }
      });
      return response;
    } catch (error) {
      console.error('Error updating product visibility:', error);
      return {
        success: false,
        error: error.message || 'Failed to update product visibility'
      };
    }
  },

  // Claim all unclaimed products (backfill for legacy products)
  claimAllProducts: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', '/api/products/claim-all', { headers });
      return response;
    } catch (error) {
      console.error('Error claiming all products:', error);
      return {
        success: false,
        error: error.message || 'Failed to claim products'
      };
    }
  },

  // ============================================
  // SAVED DESIGNS (cloud storage)
  // ============================================

  // Get all saved designs for the current user
  getSavedDesigns: async () => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, designs: [], error: 'Not authenticated' };
      }
      const response = await API.get('printifyapi', '/api/designs', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching saved designs:', error);
      return {
        success: false,
        designs: [],
        error: error.message || 'Failed to fetch saved designs'
      };
    }
  },

  // Save a design to the cloud
  saveDesign: async (designData) => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await API.post('printifyapi', '/api/designs', {
        headers,
        body: designData
      });
      return response;
    } catch (error) {
      console.error('Error saving design:', error);
      return {
        success: false,
        error: error.message || 'Failed to save design'
      };
    }
  },

  // Delete a saved design
  deleteDesign: async (designId) => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await API.del('printifyapi', `/api/designs/${designId}`, { headers });
      return response;
    } catch (error) {
      console.error('Error deleting design:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete design'
      };
    }
  },

  // Upload design image to S3 - handles both small and large images
  // For large images (>5MB), uses presigned URL for direct S3 upload
  // For small images, uses the original API endpoint
  // Returns: { success, url (presigned GET), s3Key }
  uploadDesignImage: async (dataUrl) => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, error: 'Not authenticated' };
      }

      // Calculate approximate size of base64 data
      // Base64 is ~4/3 the size of binary, so we check the string length
      const base64Data = dataUrl.split(',')[1] || '';
      const approximateSizeBytes = (base64Data.length * 3) / 4;
      const approximateSizeMB = approximateSizeBytes / (1024 * 1024);

      console.log(`Design image size: ~${approximateSizeMB.toFixed(2)}MB`);

      // For large images (>5MB), use presigned URL upload to bypass API Gateway limits
      if (approximateSizeMB > 5) {
        console.log('Large image detected, using presigned URL upload...');
        return await amplifyApi.uploadDesignImagePresigned(dataUrl);
      }

      // For smaller images, use the original endpoint
      const response = await API.post('printifyapi', '/api/designs/upload-image', {
        headers,
        body: { image: dataUrl }
      });
      return response;
    } catch (error) {
      console.error('Error uploading design image:', error);

      // If we get a 413 error, try the presigned URL approach
      if (error.response?.status === 413 || error.message?.includes('413')) {
        console.log('Got 413 error, retrying with presigned URL upload...');
        return await amplifyApi.uploadDesignImagePresigned(dataUrl);
      }

      return {
        success: false,
        error: error.message || 'Failed to upload image'
      };
    }
  },

  // Upload large design image using presigned S3 URL (bypasses API Gateway limits)
  uploadDesignImagePresigned: async (dataUrl) => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, error: 'Not authenticated' };
      }

      // Extract content type and extension from data URL
      const matches = dataUrl.match(/^data:image\/(\w+);base64,(.+)$/);
      if (!matches) {
        return { success: false, error: 'Invalid image format' };
      }

      const fileExtension = matches[1]; // png, jpeg, webp
      const base64Data = matches[2];
      const contentType = `image/${fileExtension}`;

      // Step 1: Get presigned URL from backend
      console.log('Getting presigned upload URL...');
      const urlResponse = await API.post('printifyapi', '/api/designs/get-upload-url', {
        headers,
        body: { contentType, fileExtension }
      });

      if (!urlResponse.success || !urlResponse.uploadUrl) {
        return { success: false, error: urlResponse.error || 'Failed to get upload URL' };
      }

      // Step 2: Convert base64 to blob for upload
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: contentType });

      console.log(`Uploading ${(blob.size / 1024 / 1024).toFixed(2)}MB directly to S3...`);

      // Step 3: Upload directly to S3 using presigned URL
      const uploadResponse = await fetch(urlResponse.uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: {
          'Content-Type': contentType
        }
      });

      if (!uploadResponse.ok) {
        console.error('S3 upload failed:', uploadResponse.status, uploadResponse.statusText);
        return { success: false, error: `S3 upload failed: ${uploadResponse.statusText}` };
      }

      console.log('Successfully uploaded to S3:', urlResponse.s3Key);

      return {
        success: true,
        url: urlResponse.downloadUrl,  // Presigned GET URL for display
        s3Key: urlResponse.s3Key       // S3 key for storage in database
      };
    } catch (error) {
      console.error('Error with presigned upload:', error);
      return {
        success: false,
        error: error.message || 'Failed to upload image'
      };
    }
  },

  // ============================================
  // PROXY LINKS / AFFILIATE SHARING
  // ============================================

  // Create a proxy link for sharing a product (affiliate tracking)
  createProxyLink: async (productId) => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await API.post('printifyapi', '/api/proxy-links', {
        headers,
        body: { productId }
      });
      return response;
    } catch (error) {
      console.error('Error creating proxy link:', error);
      return {
        success: false,
        error: error.message || 'Failed to create share link'
      };
    }
  },

  // Get user's proxy links with stats
  getMyProxyLinks: async () => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, links: [], error: 'Not authenticated' };
      }
      const response = await API.get('printifyapi', '/api/proxy-links/me', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching proxy links:', error);
      return {
        success: false,
        links: [],
        error: error.message || 'Failed to fetch share links'
      };
    }
  },

  // Set default mockup image for a product
  setDefaultMockup: async (productId, mockupIndex) => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await API.put('printifyapi', `/api/products/${productId}/default-mockup`, {
        headers,
        body: { mockupIndex }
      });
      return response;
    } catch (error) {
      console.error('Error setting default mockup:', error);
      return {
        success: false,
        error: error.message || 'Failed to set default mockup'
      };
    }
  },

  // ============================================
  // STRIPE CHECKOUT (payment integration)
  // ============================================

  // Get shipping cost estimate
  getShippingEstimate: async (productId, variantId, country, zip) => {
    try {
      const params = new URLSearchParams({
        productId,
        variantId: variantId.toString(),
        country,
        zip
      });
      const response = await API.get('printifyapi', `/api/checkout/shipping-estimate?${params}`, {});
      return response;
    } catch (error) {
      console.error('Error getting shipping estimate:', error);
      return {
        success: false,
        error: error.message || 'Failed to get shipping estimate'
      };
    }
  },

  // Create Stripe checkout session
  createCheckoutSession: async (checkoutData) => {
    try {
      const response = await API.post('printifyapi', '/api/checkout/create-session', {
        body: checkoutData
      });
      return response;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return {
        success: false,
        error: error.message || 'Failed to initiate checkout'
      };
    }
  },

  // Get order by Stripe session ID (for success page)
  getOrderBySession: async (sessionId) => {
    try {
      const response = await API.get('printifyapi', `/api/orders/session/${sessionId}`, {});
      return response;
    } catch (error) {
      console.error('Error getting order by session:', error);
      return {
        success: false,
        error: error.message || 'Failed to get order'
      };
    }
  },

  // Guest order lookup by email and order ID
  lookupOrder: async (email, orderId) => {
    try {
      const params = new URLSearchParams({ email, orderId });
      const response = await API.get('printifyapi', `/api/orders/lookup?${params}`, {});
      return response;
    } catch (error) {
      console.error('Error looking up order:', error);
      return {
        success: false,
        error: error.message || 'Failed to find order'
      };
    }
  },

  // ============================================
  // ADMIN API METHODS
  // ============================================

  // Check if current user is an admin
  checkAdminStatus: async () => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, isAdmin: false, error: 'Not authenticated' };
      }
      const response = await API.get('printifyapi', '/api/admin/me', { headers });
      return response;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return { success: false, isAdmin: false, error: error.message };
    }
  },

  // Get all pricing configuration
  getAdminPricing: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', '/api/admin/pricing', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching pricing config:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a pricing rule
  // ruleId format: "configType/configId" e.g., "tier/economy" or "category/drinkware"
  updatePricingRule: async (configType, configId, ruleData) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', `/api/admin/pricing/${configType}/${configId}`, {
        headers,
        body: ruleData
      });
      return response;
    } catch (error) {
      console.error('Error updating pricing rule:', error);
      return { success: false, error: error.message };
    }
  },

  // Toggle pricing matrix on/off
  togglePricingMatrix: async (enabled) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', '/api/admin/pricing/toggle-matrix', {
        headers,
        body: { enabled }
      });
      return response;
    } catch (error) {
      console.error('Error toggling pricing matrix:', error);
      return { success: false, error: error.message };
    }
  },

  // Preview pricing calculation
  previewPricing: async (baseCost, category) => {
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ baseCost: baseCost.toString(), category: category || '' });
      const response = await API.get('printifyapi', `/api/admin/pricing/preview?${params}`, { headers });
      return response;
    } catch (error) {
      console.error('Error previewing pricing:', error);
      return { success: false, error: error.message };
    }
  },

  // Get AI usage stats (admin)
  getAdminAiStats: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', '/api/admin/ai-stats', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching AI stats:', error);
      return { success: false, error: error.message };
    }
  },

  // ============================================
  // USER LIMITS
  // ============================================

  // Get current user's limits and usage
  getUserLimits: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', '/api/limits/me', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching user limits:', error);
      return { success: false, error: error.message };
    }
  },

  // ============================================
  // SUBSCRIPTION METHODS (Tier Upgrades)
  // ============================================

  // Create subscription checkout session
  createSubscriptionCheckout: async (tier) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', '/api/subscriptions/checkout', {
        headers,
        body: { tier }
      });
      return response;
    } catch (error) {
      console.error('Error creating subscription checkout:', error);
      return { success: false, error: error.message };
    }
  },

  // Create customer portal session for subscription management
  createSubscriptionPortal: async (returnUrl = null) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', '/api/subscriptions/portal', {
        headers,
        body: { returnUrl }
      });
      return response;
    } catch (error) {
      console.error('Error creating subscription portal:', error);
      return { success: false, error: error.message };
    }
  },

  // Get current subscription status
  getSubscriptionStatus: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', '/api/subscriptions/status', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      return { success: false, error: error.message };
    }
  },

  // ============================================
  // ADMIN TIER MANAGEMENT
  // ============================================

  // Get all tier limit configurations (admin)
  getAdminTierLimits: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', '/api/admin/limits/tiers', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching tier limits:', error);
      return { success: false, error: error.message };
    }
  },

  // Update tier limits (admin)
  updateAdminTierLimits: async (tier, limits) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', `/api/admin/limits/tiers/${tier}`, {
        headers,
        body: limits
      });
      return response;
    } catch (error) {
      console.error('Error updating tier limits:', error);
      return { success: false, error: error.message };
    }
  },

  // Update user's tier (admin)
  updateUserTier: async (userId, tier, expiresAt = null) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', `/api/admin/users/${userId}/tier`, {
        headers,
        body: { tier, expiresAt }
      });
      return response;
    } catch (error) {
      console.error('Error updating user tier:', error);
      return { success: false, error: error.message };
    }
  },

  // Update user's limit overrides (admin)
  updateUserLimitOverrides: async (userId, overrides) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', `/api/admin/users/${userId}/limits`, {
        headers,
        body: overrides
      });
      return response;
    } catch (error) {
      console.error('Error updating user limit overrides:', error);
      return { success: false, error: error.message };
    }
  },

  // Get user's tier info (admin)
  getAdminUserTier: async (userId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', `/api/admin/users/${userId}/tier`, { headers });
      return response;
    } catch (error) {
      console.error('Error fetching user tier info:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all users with admin info
  getAdminUsers: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', '/api/admin/users', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching admin users:', error);
      return { success: false, error: error.message };
    }
  },

  // Grant admin access to a user
  grantAdminAccess: async (userId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', `/api/admin/users/${userId}/grant`, {
        headers,
        body: {}
      });
      return response;
    } catch (error) {
      console.error('Error granting admin access:', error);
      return { success: false, error: error.message };
    }
  },

  // Revoke admin access from a user
  revokeAdminAccess: async (userId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.del('printifyapi', `/api/admin/users/${userId}/revoke`, { headers });
      return response;
    } catch (error) {
      console.error('Error revoking admin access:', error);
      return { success: false, error: error.message };
    }
  },

  // Get commission ledger
  getCommissionLedger: async (status = null, limit = 50) => {
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ limit: limit.toString() });
      if (status) params.append('status', status);
      const response = await API.get('printifyapi', `/api/admin/commissions?${params}`, { headers });
      return response;
    } catch (error) {
      console.error('Error fetching commission ledger:', error);
      return { success: false, error: error.message };
    }
  },

  // Update commission status
  updateCommissionStatus: async (commissionId, status) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', `/api/admin/commissions/${commissionId}`, {
        headers,
        body: { status }
      });
      return response;
    } catch (error) {
      console.error('Error updating commission status:', error);
      return { success: false, error: error.message };
    }
  },

  // Get admin commission rates configuration
  getAdminCommissionRates: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', '/api/admin/commission-rates', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching admin commission rates:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a commission rate (admin)
  updateCommissionRate: async (configId, updates) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', `/api/admin/commission-rates/${configId}`, {
        headers,
        body: updates
      });
      return response;
    } catch (error) {
      console.error('Error updating commission rate:', error);
      return { success: false, error: error.message };
    }
  },

  // ============================================
  // ADMIN USER APPROVAL METHODS
  // ============================================

  // Get all users (not just admins) with approval status
  adminGetAllUsers: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', '/api/admin/users/all', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching all users:', error);
      return { success: false, error: error.message };
    }
  },

  // Approve a user (allow their public products to be visible)
  adminApproveUser: async (userId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', `/api/admin/users/${userId}/approve`, {
        headers,
        body: {}
      });
      return response;
    } catch (error) {
      console.error('Error approving user:', error);
      return { success: false, error: error.message };
    }
  },

  // Restrict a user (hide their public products from gallery)
  adminRestrictUser: async (userId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', `/api/admin/users/${userId}/restrict`, {
        headers,
        body: {}
      });
      return response;
    } catch (error) {
      console.error('Error restricting user:', error);
      return { success: false, error: error.message };
    }
  },

  // ============================================
  // ADMIN PRODUCT MODERATION METHODS
  // ============================================

  // Hide a product (admin override)
  adminHideProduct: async (productId, reason = null) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', `/api/admin/products/${productId}/hide`, {
        headers,
        body: { reason }
      });
      return response;
    } catch (error) {
      console.error('Error hiding product:', error);
      return { success: false, error: error.message };
    }
  },

  // Unhide a product
  adminUnhideProduct: async (productId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', `/api/admin/products/${productId}/unhide`, {
        headers,
        body: {}
      });
      return response;
    } catch (error) {
      console.error('Error unhiding product:', error);
      return { success: false, error: error.message };
    }
  },

  // Get all admin-hidden products
  adminGetHiddenProducts: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', '/api/admin/products/hidden', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching hidden products:', error);
      return { success: false, error: error.message };
    }
  },

  // ============================================
  // USER PROFILE API METHODS
  // ============================================

  // Get user's commission profile and stats
  getMyCommissions: async () => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await API.get('printifyapi', '/api/commissions/me', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching user commissions:', error);
      return { success: false, error: error.message };
    }
  },

  // Get full commission history
  getMyCommissionHistory: async (limit = 50) => {
    try {
      const headers = await getAuthHeaders();
      if (!headers.Authorization) {
        return { success: false, error: 'Not authenticated' };
      }
      const response = await API.get('printifyapi', `/api/commissions/me/history?limit=${limit}`, { headers });
      return response;
    } catch (error) {
      console.error('Error fetching commission history:', error);
      return { success: false, error: error.message };
    }
  },

  // Get current commission rates (public endpoint - no auth required)
  getCommissionRates: async () => {
    try {
      const response = await API.get('printifyapi', '/api/commission-rates', {});
      return response;
    } catch (error) {
      console.error('Error fetching commission rates:', error);
      // Return default values as fallback to prevent UI breaking
      return {
        success: false,
        error: error.message,
        creatorPercent: 10,
        referralPercent: 5,
        minPayoutAmount: 25
      };
    }
  },

  // ============================================
  // EXTERNAL ART LIBRARY APIs
  // ============================================

  // Search Metropolitan Museum of Art collection
  // No auth required - public API (80 requests/sec limit)
  // Returns objectIDs for pagination support
  searchMetArt: async (query, limit = 20, offset = 0) => {
    try {
      const searchUrl = `https://collectionapi.metmuseum.org/public/collection/v1/search?q=${encodeURIComponent(query)}&hasImages=true`;
      const searchResult = await fetch(searchUrl);
      const { objectIDs = [] } = await searchResult.json();

      if (!objectIDs || objectIDs.length === 0) {
        return { success: true, artworks: [], total: 0, hasMore: false };
      }

      // Fetch objects for the current page (offset to offset+limit)
      const objectsToFetch = objectIDs.slice(offset, offset + limit);
      const details = await Promise.all(
        objectsToFetch.map(async (id) => {
          try {
            const res = await fetch(`https://collectionapi.metmuseum.org/public/collection/v1/objects/${id}`);
            return res.json();
          } catch (err) {
            console.warn(`Failed to fetch Met object ${id}:`, err);
            return null;
          }
        })
      );

      // Filter to only items with images and transform to consistent format
      const artworks = details
        .filter(d => d && d.primaryImageSmall)
        .map(d => ({
          id: d.objectID,
          title: d.title || 'Untitled',
          artist: d.artistDisplayName || 'Unknown Artist',
          date: d.objectDate || '',
          medium: d.medium || '',
          thumbnailUrl: d.primaryImageSmall,
          imageUrl: d.primaryImage,
          source: 'met-museum',
          attribution: `${d.artistDisplayName || 'Unknown'}, ${d.objectDate || ''} - The Metropolitan Museum of Art`,
          isPublicDomain: d.isPublicDomain
        }));

      return {
        success: true,
        artworks,
        total: objectIDs.length,
        offset: offset,
        hasMore: offset + limit < objectIDs.length
      };
    } catch (error) {
      console.error('Error searching Met Museum:', error);
      return {
        success: false,
        artworks: [],
        error: error.message || 'Failed to search art library'
      };
    }
  },

  // Search Rawpixel public domain images
  // Uses backend proxy to avoid CORS issues (Rawpixel API blocks browser requests)
  // Backend uses freecc0=1 to request only CC0 public domain images
  searchRawpixel: async (query, limit = 20, page = 1) => {
    try {
      // Use our backend proxy to avoid CORS issues with Rawpixel API
      // Backend adds freecc0=1 and premium=free to filter for CC0 public domain images only
      const data = await API.get('printifyapi', `/api/library/rawpixel/search?keys=${encodeURIComponent(query)}&page=${page}`, {});

      if (!data.results || data.results.length === 0) {
        return { success: true, artworks: [], total: 0, hasMore: false };
      }

      // Transform results to consistent format
      // Rawpixel API returns: id, url, metadata.title, metadata.description, style_uri, google_teaser
      // style_uri has {size} placeholder for different resolutions
      const artworks = data.results
        .filter(item => item.style_uri || item.google_teaser)
        .slice(0, limit)
        .map(item => {
          // Build thumbnail URL - use google_teaser or replace {size} in style_uri
          const thumbnailUrl = item.google_teaser ||
            (item.style_uri ? item.style_uri.replace('{}', 'image_400') : null);

          // Build full-size image URL - try highest resolution first, with fallbacks
          // Rawpixel has: image_4000, image_2000, image_1000, image_800, image_600, image_400
          // Not all sizes exist for all images, so we provide multiple fallbacks
          const imageUrl = item.style_uri ?
            item.style_uri.replace('{}', 'image_4000') :
            null;
          // Fallback chain: 2000 -> 1000 -> 800 (for images without high-res versions)
          const imageUrlFallbacks = item.style_uri ? [
            item.style_uri.replace('{}', 'image_2000'),
            item.style_uri.replace('{}', 'image_1000'),
            item.style_uri.replace('{}', 'image_800')
          ] : [];

          // Extract title from metadata
          const title = item.metadata?.title?.replace(' | Free Photo Illustration - rawpixel', '') ||
                        item.metadata?.description_text ||
                        'Untitled';

          return {
            id: item.id,
            title: title,
            artist: item.metadata?.licenseSource || 'Rawpixel',
            date: '',
            thumbnailUrl,
            imageUrl,
            imageUrlFallbacks,  // Array of fallback URLs: 2000px, 1000px, 800px
            source: 'rawpixel',
            attribution: `${item.metadata?.licenseSource || 'Rawpixel'} - CC0 Public Domain`,
            isPublicDomain: true,
            landingUrl: item.url
          };
        });

      return {
        success: true,
        artworks,
        total: data.total || artworks.length,
        page: page,
        hasMore: data.results.length >= 20 // Rawpixel returns ~100 per page but we limit
      };
    } catch (error) {
      console.error('Error searching Rawpixel:', error);
      return {
        success: false,
        artworks: [],
        error: error.message || 'Failed to search Rawpixel'
      };
    }
  },

  // Search World Illustrated GraphQL API for photos
  // channelId and channelTitle are optional - both required for category filtering to work
  searchWorldIllustrated: async (query, limit = 20, page = 1, channelId = null, channelTitle = null) => {
    try {
      // Use our backend proxy to handle GraphQL request
      let url = `/api/library/world-illustrated/search?query=${encodeURIComponent(query)}&page=${page}`;
      if (channelId && channelTitle) {
        url += `&channelId=${channelId}&channelTitle=${encodeURIComponent(channelTitle)}`;
      }
      const data = await API.get('printifyapi', url, {});

      const results = data.data?.search?.userUploadedContent || [];
      const total = data.data?.search?.allResultsCount || 0;

      if (!results || results.length === 0) {
        return { success: true, artworks: [], total: 0, hasMore: false };
      }

      // Transform results to consistent format
      // Filter to only include items with high-res pathToFileCompressed (required for printing)
      // Note: contentSetId can have duplicates, so we use content.id as the unique identifier
      const artworks = results
        .filter(item => item.content?.pathToFileThumbnail && item.content?.pathToFileCompressed && item.content?.id)
        .slice(0, limit)
        .map(item => ({
          id: item.content.id,  // Use contentId as unique ID (contentSetId has duplicates)
          contentId: item.content.id,  // Content ID for fetching signed URL
          contentSetId: item.contentSetId,  // Keep for reference
          title: item.content?.headline || item.content?.objectName || 'Untitled',
          artist: 'World Illustrated',
          date: item.content?.publishDate || '',
          thumbnailUrl: item.content?.pathToFileThumbnail,
          imageUrl: item.content?.pathToFileCompressed,  // Preview URL - use getWorldIllustratedSignedUrl for high-res
          source: 'world-illustrated',
          attribution: 'World Illustrated',
          isPublicDomain: true
        }));

      return {
        success: true,
        artworks,
        total: total,
        page: page,
        hasMore: results.length >= 20
      };
    } catch (error) {
      console.error('Error searching World Illustrated:', error);
      return {
        success: false,
        artworks: [],
        error: error.message || 'Failed to search World Illustrated'
      };
    }
  },

  // Get signed URL for high-resolution World Illustrated image
  // This retrieves the actual high-res file for printing (not the compressed preview)
  getWorldIllustratedSignedUrl: async (contentId) => {
    try {
      if (!contentId) {
        return { success: false, error: 'Content ID is required' };
      }

      const data = await API.get('printifyapi', `/api/library/world-illustrated/signed-url?contentId=${contentId}`, {});

      if (data.success && data.signedURL) {
        return {
          success: true,
          contentId: data.contentId,
          signedURL: data.signedURL
        };
      } else {
        return {
          success: false,
          error: data.error || 'Failed to get signed URL'
        };
      }
    } catch (error) {
      console.error('Error getting World Illustrated signed URL:', error);
      return {
        success: false,
        error: error.message || 'Failed to get signed URL from World Illustrated'
      };
    }
  },

  // ============================================
  // MONETIZATION
  // ============================================

  // Get monetization content for creation wait screen (public)
  getMonetizationContent: async () => {
    try {
      let headers = {};
      try { headers = await getAuthHeaders(); } catch (e) { /* optional auth */ }
      const response = await API.get('printifyapi', '/api/monetization/content', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching monetization content:', error);
      return { success: false, enabled: false };
    }
  },

  // Track promo impression or click (fire-and-forget)
  trackMonetizationEvent: async (promoId, event) => {
    try {
      let headers = {};
      try { headers = await getAuthHeaders(); } catch (e) { /* optional auth */ }
      await API.post('printifyapi', '/api/monetization/track', {
        headers,
        body: { promoId, event }
      });
    } catch (error) {
      // Silent failure - tracking should never block UX
      console.error('Error tracking monetization event:', error);
    }
  },

  // ============================================
  // ADMIN MONETIZATION
  // ============================================

  // Get full monetization config (admin)
  getAdminMonetization: async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.get('printifyapi', '/api/admin/monetization', { headers });
      return response;
    } catch (error) {
      console.error('Error fetching admin monetization:', error);
      return { success: false, error: error.message };
    }
  },

  // Update monetization settings (admin)
  updateMonetizationSettings: async (settings) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', '/api/admin/monetization/settings', {
        headers,
        body: settings
      });
      return response;
    } catch (error) {
      console.error('Error updating monetization settings:', error);
      return { success: false, error: error.message };
    }
  },

  // Create a promo (admin)
  createMonetizationPromo: async (promoData) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', '/api/admin/monetization/promos', {
        headers,
        body: promoData
      });
      return response;
    } catch (error) {
      console.error('Error creating promo:', error);
      return { success: false, error: error.message };
    }
  },

  // Update a promo (admin)
  updateMonetizationPromo: async (promoId, promoData) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.put('printifyapi', `/api/admin/monetization/promos/${promoId}`, {
        headers,
        body: promoData
      });
      return response;
    } catch (error) {
      console.error('Error updating promo:', error);
      return { success: false, error: error.message };
    }
  },

  // Delete a promo (admin)
  deleteMonetizationPromo: async (promoId) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.del('printifyapi', `/api/admin/monetization/promos/${promoId}`, {
        headers
      });
      return response;
    } catch (error) {
      console.error('Error deleting promo:', error);
      return { success: false, error: error.message };
    }
  },

  // Upload promo image (admin) - returns presigned URL
  uploadMonetizationImage: async (contentType, fileExtension) => {
    try {
      const headers = await getAuthHeaders();
      const response = await API.post('printifyapi', '/api/admin/monetization/upload-image', {
        headers,
        body: { contentType, fileExtension }
      });
      return response;
    } catch (error) {
      console.error('Error getting promo upload URL:', error);
      return { success: false, error: error.message };
    }
  }
};

// Mock API for fallback
export const mockApi = {
  uploadImage: async (file) => {
    await new Promise(resolve => setTimeout(resolve, 1500));

    return {
      success: true,
      image: {
        id: `img_${Date.now()}`,
        url: URL.createObjectURL(file),
        printifyImageId: `printify_${Date.now()}`,
        width: 800,
        height: 800,
        aspectRatio: 1,
        aspectRatioType: 'square'
      }
    };
  },

  createProducts: async (imageId) => {
    await new Promise(resolve => setTimeout(resolve, 2000));

    return {
      success: true,
      products: [
        {
          id: `prod_${Date.now()}_1`,
          title: 'Classic T-Shirt',
          price: 19.99,
          mockupUrl: 'https://via.placeholder.com/300x400/7c3aed/ffffff?text=T-Shirt',
          printifyProductId: `printify_prod_1`
        },
        {
          id: `prod_${Date.now()}_2`,
          title: 'Coffee Mug',
          price: 14.99,
          mockupUrl: 'https://via.placeholder.com/300x300/ec4899/ffffff?text=Mug',
          printifyProductId: `printify_prod_2`
        },
        {
          id: `prod_${Date.now()}_3`,
          title: 'Beach Towel',
          price: 24.99,
          mockupUrl: 'https://via.placeholder.com/400x300/8b5cf6/ffffff?text=Towel',
          printifyProductId: `printify_prod_3`
        }
      ]
    };
  },

  initiateCheckout: async (productId) => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    return {
      success: true,
      checkoutUrl: `https://example-store.printify.me/products/${productId}?test=true`
    };
  },

  // Mock getPublishedProducts
  getPublishedProducts: async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return {
      success: true,
      products: [
        {
          id: `prod_${Date.now()}_1`,
          title: 'Premium Cotton T-Shirt',
          description: 'A comfortable, soft cotton t-shirt with your custom design.',
          price: 24.99,
          mockupUrl: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          createdAt: new Date().toISOString(),
          status: 'published',
          printifyProductId: 'printify_1'
        },
        {
          id: `prod_${Date.now()}_2`,
          title: 'Ceramic Coffee Mug',
          description: '11oz ceramic mug, microwave and dishwasher safe.',
          price: 15.99,
          mockupUrl: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
          createdAt: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
          status: 'published',
          printifyProductId: 'printify_2'
        }
      ]
    };
  },

  // Mock getProductColors
  getProductColors: async (productId) => {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      colors: [
        { name: 'White', hex: '#FFFFFF', variants: [1, 2, 3] },
        { name: 'Black', hex: '#000000', variants: [4, 5, 6] },
        { name: 'Navy', hex: '#000080', variants: [7, 8, 9] },
        { name: 'Heather Grey', hex: '#808080', variants: [10, 11, 12] }
      ]
    };
  },

  // Mock deleteProduct
  deleteProduct: async (productId) => {
    await new Promise(resolve => setTimeout(resolve, 800));
    return { success: true };
  },

  // Mock publishProduct
  publishProduct: async (productId) => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    return { success: true };
  },

  // Mock createOrder
  createOrder: async (productId, variantId, customerInfo, quantity) => {
    await new Promise(resolve => setTimeout(resolve, 2000));
    return {
      success: true,
      order: {
        id: `order_${Date.now()}`,
        external_id: `EXT-${Math.floor(Math.random() * 10000)}`,
        total: (quantity * 24.99).toFixed(2),
        status: 'pending'
      }
    };
  },

  // Mock getOrder
  getOrder: async (orderId) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return {
      success: true,
      order: {
        id: orderId,
        external_id: `EXT-${Math.floor(Math.random() * 10000)}`,
        status: 'pending',
        items: []
      }
    };
  },

  // Mock getOrders
  getOrders: async (page, limit) => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return {
      success: true,
      orders: []
    };
  }
};

// Export the real API (with fallback to mock if errors occur)
const api = amplifyApi;
export default api;